import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  Typography,
  Divider,
  Alert,
  FormControlLabel,
  Switch,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  CopyAll as CopyAllIcon,
  Security as SecurityIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  SwapHoriz as SwapHorizIcon,
  Add as AddIcon
} from '@mui/icons-material';

import { 
  escritorioService, 
  Escritorio, 
  EscritorioFormData 
} from '../../services/escritorioService';
import EscritorioSelect from '../selects/EscritorioSelect';
import EstadosBrSelect from '../selects/EstadosBrSelect';
import { buscarCep, formatarCepOnInput, formatarPorcentagem, formatarPorcentagemOnInput, parsearMoeda } from '../../utils/formatToView';

interface EscritorioFormProps {
  escritorio?: Escritorio | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`escritorio-tabpanel-${index}`}
      aria-labelledby={`escritorio-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 0 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const EscritorioForm: React.FC<EscritorioFormProps> = ({
  escritorio,
  onSuccess,
  onCancel
}) => {
  const [formData, setFormData] = useState<EscritorioFormData>({
    identificacao: '',
    tipo: 'filial',
    nome_fantasia: '',
    razao_social: '',
    cnpj: '',
    fistel_anatel: '',
    inscricao_estadual: '',
    regime_tributario: 'simples_nacional',
    cep: '',
    codigo_municipio: '',
    cidade: '',
    estado: '',
    endereco: '',
    bairro: '',
    numero: '',
    complemento: '',
    telefone: '',
    email: '',
    responsavel: '',
    is_default: false,
    // Campos fiscais
    aliquota_icms: undefined,
    aliquota_pis: undefined,
    aliquota_cofins: undefined,
    aliquota_fust: undefined,
    aliquota_funttel: undefined,
    aliquota_ibsuf: undefined,
    aliquota_ibsmun: undefined,
    aliquota_cbs: undefined,
    cst_icms: '',
    cst_pis_cofins: '',
    cst_cbs_ibs: '',
    c_class_trib_cbs_ibs: '',
    msg_rodape: '',
    descricao_padrao: '',
    num_proxima_nota: 1
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cepLoading, setCepLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [certificadoFile, setCertificadoFile] = useState<File | null>(null);
  const [certificadoSenha, setCertificadoSenha] = useState('');
  const [certificadoValidado, setCertificadoValidado] = useState(false);
  const [validandoCertificado, setValidandoCertificado] = useState(false);
  const [mostrarFormCertificado, setMostrarFormCertificado] = useState(false);
  
  // Estado para o modal de herdar dados
  const [modalHerdarDados, setModalHerdarDados] = useState(false);
  const [escritorioSelecionado, setEscritorioSelecionado] = useState<number | null>(null);
  const [carregandoHeranca, setCarregandoHeranca] = useState(false);

  // Popular dados se está editando
  useEffect(() => {
    if (escritorio) {
      const cstCbsIbs = escritorio.cst_cbs_ibs || '';
      const classOpts = escritorioService.getCClassTribCbsIbsOptions(cstCbsIbs);
      const classSaved = escritorio.c_class_trib_cbs_ibs || '';
      const cClassTrib =
        classSaved && classOpts.some((o) => o.value === classSaved) ? classSaved : '';

      setFormData({
        identificacao: escritorio.identificacao,
        tipo: escritorio.tipo,
        nome_fantasia: escritorio.nome_fantasia,
        razao_social: escritorio.razao_social,
        cnpj: escritorio.cnpj,
        fistel_anatel: escritorio.fistel_anatel || '',
        inscricao_estadual: escritorio.inscricao_estadual || '',
        regime_tributario: escritorio.regime_tributario || 'simples_nacional',
        cep: escritorio.cep,
        codigo_municipio: escritorio.codigo_municipio || '',
        cidade: escritorio.cidade,
        estado: escritorio.estado,
        endereco: escritorio.endereco,
        bairro: escritorio.bairro,
        numero: escritorio.numero || '',
        complemento: escritorio.complemento || '',
        telefone: escritorio.telefone || '',
        email: escritorio.email || '',
        responsavel: escritorio.responsavel || '',
        is_default: escritorio.is_default,
        // Campos fiscais
        aliquota_icms: escritorio.aliquota_icms || undefined,
        aliquota_pis: escritorio.aliquota_pis || undefined,
        aliquota_cofins: escritorio.aliquota_cofins || undefined,
        aliquota_fust: escritorio.aliquota_fust || undefined,
        aliquota_funttel: escritorio.aliquota_funttel || undefined,
        aliquota_ibsuf: escritorio.aliquota_ibsuf || undefined,
        aliquota_ibsmun: escritorio.aliquota_ibsmun || undefined,
        aliquota_cbs: escritorio.aliquota_cbs || undefined,
        cst_icms: escritorio.cst_icms || '',
        cst_pis_cofins: escritorio.cst_pis_cofins || '',
        cst_cbs_ibs: cstCbsIbs,
        c_class_trib_cbs_ibs: cClassTrib,
        msg_rodape: escritorio.msg_rodape || '',
        descricao_padrao: escritorio.descricao_padrao || '',
        num_proxima_nota: escritorio.num_proxima_nota || 1
      });
    }
  }, [escritorio]);

  const handleInputChange = (field: keyof EscritorioFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleCnpjChange = (value: string) => {
    const formatted = escritorioService.formatCNPJ(value);
    handleInputChange('cnpj', formatted);
  };

  const handleTelefoneChange = (value: string) => {
    const formatted = escritorioService.formatTelefone(value);
    handleInputChange('telefone', formatted);
  };

  const handleBuscarCep = async () => {
    if (!formData.cep || formData.cep.length < 8) {
      setError('CEP deve ter 8 dígitos');
      return;
    }

    // Função para atualizar os valores do formulário
    const setValue = (field: string, value: any) => {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    };

    // Usar a função utilitária buscarCep
    await buscarCep(formData.cep, setValue, setError, setCepLoading);
  };

  // Função para herdar dados de outro escritório
  const handleHerdarDados = async () => {
    if (!escritorioSelecionado) {
      setError('Selecione um escritório para herdar os dados');
      return;
    }

    setCarregandoHeranca(true);
    setError(null);

    try {
      const response = await escritorioService.getEscritorioById(escritorioSelecionado);
      
      if (response.success && response.data) {
        const escritorioOrigem = response.data;
        
        // Importar apenas os campos especificados
        setFormData(prev => ({
          ...prev,
          nome_fantasia: escritorioOrigem.nome_fantasia,
          razao_social: escritorioOrigem.razao_social,
          cnpj: escritorioOrigem.cnpj || '', // CNPJ pode ser copiado (não é mais único)
          fistel_anatel: escritorioOrigem.fistel_anatel || '',
          inscricao_estadual: escritorioOrigem.inscricao_estadual || '',
          regime_tributario: escritorioOrigem.regime_tributario || 'simples_nacional',
          responsavel: escritorioOrigem.responsavel || '',
          // campos fiscais
          aliquota_icms: escritorioOrigem.aliquota_icms || undefined,
          aliquota_pis: escritorioOrigem.aliquota_pis || undefined,
          aliquota_cofins: escritorioOrigem.aliquota_cofins || undefined,
          aliquota_fust: escritorioOrigem.aliquota_fust || undefined,
          aliquota_funttel: escritorioOrigem.aliquota_funttel || undefined,
          aliquota_ibsuf: escritorioOrigem.aliquota_ibsuf || undefined,
          aliquota_ibsmun: escritorioOrigem.aliquota_ibsmun || undefined,
          aliquota_cbs: escritorioOrigem.aliquota_cbs || undefined,
          cst_icms: escritorioOrigem.cst_icms || '',
          cst_pis_cofins: escritorioOrigem.cst_pis_cofins || '',
          cst_cbs_ibs: escritorioOrigem.cst_cbs_ibs || '',
          c_class_trib_cbs_ibs: escritorioOrigem.c_class_trib_cbs_ibs || '',
          msg_rodape: escritorioOrigem.msg_rodape || '',
          descricao_padrao: escritorioOrigem.descricao_padrao || '',
          num_proxima_nota: escritorioOrigem.num_proxima_nota || 1
        }));

        // Fechar modal
        setModalHerdarDados(false);
        setEscritorioSelecionado(null);
        
        // Exibir sucesso
        setError(null);
      } else {
        throw new Error(response.message || 'Erro ao carregar dados do escritório');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao herdar dados do escritório');
    } finally {
      setCarregandoHeranca(false);
    }
  };

  const handleValidarCertificado = async () => {
    if (!certificadoFile) {
      setError('Selecione o arquivo do certificado digital A1');
      return;
    }
    if (!certificadoSenha.trim()) {
      setError('Informe a senha do certificado');
      return;
    }

    setValidandoCertificado(true);
    setError(null);

    try {
      const response = await escritorioService.validarCertificado(certificadoFile, certificadoSenha);
      if (response.success) {
        setCertificadoValidado(true);
        setError(null);
      } else {
        setCertificadoValidado(false);
        setError((response as any).message || 'Erro ao validar certificado');
      }
    } catch (err: any) {
      setCertificadoValidado(false);
      setError(err.response?.data?.message || err.message || 'Erro ao validar certificado');
    } finally {
      setValidandoCertificado(false);
    }
  };

  const validateForm = (): boolean => {
    // Campos obrigatórios
    const requiredFields = [
      'identificacao', 'nome_fantasia', 'razao_social', 'cnpj',
      'cep', 'cidade', 'estado', 'endereco', 'bairro'
    ];

    for (const field of requiredFields) {
      if (!formData[field as keyof EscritorioFormData]) {
        setError(`Campo "${field}" é obrigatório`);
        return false;
      }
    }

    // Validar CNPJ
    if (!escritorioService.validateCNPJ(formData.cnpj)) {
      setError('CNPJ inválido');
      return false;
    }

    // Validar CEP
    if (!escritorioService.validateCEP(formData.cep)) {
      setError('CEP inválido');
      return false;
    }

    // Validar email se preenchido
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Email inválido');
      return false;
    }

    // Se certificado foi selecionado, deve estar validado
    if (certificadoFile && !certificadoValidado) {
      setError('Valide o certificado digital antes de salvar');
      return false;
    }

    if (certificadoFile && !certificadoSenha.trim()) {
      setError('Informe a senha do certificado digital');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response;
      let escritorioId: number;

      if (escritorio) {
        // Editar
        response = await escritorioService.updateEscritorio(escritorio.id, formData);
        escritorioId = escritorio.id;
      } else {
        // Criar
        response = await escritorioService.createEscritorio(formData);
        escritorioId = (response as any).data?.id;
      }

      if (response.success) {
        // Se certificado foi selecionado e validado, fazer upload
        if (certificadoFile && certificadoSenha && escritorioId) {
          await escritorioService.uploadCertificado(escritorioId, certificadoFile, certificadoSenha);
        }
        onSuccess();
      } else {
        throw new Error(response.message || 'Erro ao salvar escritório');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Erro ao salvar escritório');
    } finally {
      setLoading(false);
    }
  };

  const regimesTributarios = escritorioService.getRegimesTributarios();
  const tiposEscritorio = escritorioService.getTiposEscritorio();
  const cstIcmsOptions = escritorioService.getCstIcmsOptions(formData.regime_tributario);
  const cClassTribCbsIbsOptions = useMemo(
    () => escritorioService.getCClassTribCbsIbsOptions(formData.cst_cbs_ibs),
    [formData.cst_cbs_ibs]
  );

  const handleRegimeTributarioChange = (novoRegime: string) => {
    const opcoesNovoRegime = escritorioService.getCstIcmsOptions(novoRegime);
    const cstAtualValido = opcoesNovoRegime.some(opt => opt.value === formData.cst_icms);

    setFormData(prev => ({
      ...prev,
      regime_tributario: novoRegime as EscritorioFormData['regime_tributario'],
      cst_icms: cstAtualValido ? prev.cst_icms : ''
    }));
    setError(null);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="abas do formulário de escritório">
          <Tab label="Dados Cadastrais" id="escritorio-tab-0" aria-controls="escritorio-tabpanel-0" />
          <Tab label="Fiscais" id="escritorio-tab-1" aria-controls="escritorio-tabpanel-1" />
          <Tab label="Certificado Digital" id="escritorio-tab-2" aria-controls="escritorio-tabpanel-2" />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <Grid container spacing={1}>
        {/* Dados Básicos */}
        <Grid item xs={12}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5 }}>
              Dados Básicos
            </Typography>
            
            {/* Botão Herdar Dados - apenas para novos escritórios */}
            {!escritorio && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<CopyAllIcon />}
                onClick={() => setModalHerdarDados(true)}
                disabled={loading}
                sx={{ mb: 0.5 }}
              >
                Herdar dados de outro escritório
              </Button>
            )}
          </Box>
          <Divider sx={{ mb: 1 }} />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Identificação"
            value={formData.identificacao}
            onChange={(e) => handleInputChange('identificacao', e.target.value)}
            disabled={loading}
            required={true}
            helperText="Identificação única (ex: MATRIZ, FILIAL01)"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Tipo *</InputLabel>
            <Select
              value={formData.tipo}
              label="Tipo"
              onChange={(e) => handleInputChange('tipo', e.target.value)}
              disabled={loading}
              size="small"
              required={true}
            >
              {tiposEscritorio.map((tipo) => (
                <MenuItem key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Nome Fantasia"
            value={formData.nome_fantasia}
            onChange={(e) => handleInputChange('nome_fantasia', e.target.value)}
            disabled={loading}
            required={true}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            size="small"
            label="Razão Social"
            value={formData.razao_social}
            onChange={(e) => handleInputChange('razao_social', e.target.value)}
            disabled={loading}
            required={true}
          />
        </Grid>


        {/* Contato */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5, mt: 1 }}>
            Contato
          </Typography>
          <Divider sx={{ mb: 1 }} />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Telefone"
            value={formData.telefone}
            onChange={(e) => handleTelefoneChange(e.target.value)}
            disabled={loading}
            placeholder="(00) 00000-0000"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            size="small"
            label="Responsável"
            value={formData.responsavel}
            onChange={(e) => handleInputChange('responsavel', e.target.value)}
            disabled={loading}
          />
        </Grid>

        {/* Endereço  */}
        <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5, mt: 1 }}>
              Endereço
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </Grid>

          <Grid item xs={12} md={2.5}>
            <TextField
              fullWidth
              size="small"
              label="CEP"
              value={formData.cep}
              onChange={(e) => {
                const formatted = formatarCepOnInput(e.target.value);
                handleInputChange('cep', formatted);
              }}
              disabled={loading}
              placeholder="00000-000"
              required={true}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleBuscarCep}
                      disabled={loading || cepLoading}
                      size="small"
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label="Cidade"
              value={formData.cidade}
              onChange={(e) => handleInputChange('cidade', e.target.value)}
              disabled={loading}
              required={true}
            />
          </Grid>

          <Grid item xs={12} md={2.5}>
            <EstadosBrSelect
              value={formData.estado}
              onChange={(e) => handleInputChange('estado', e.target.value)}
              disabled={loading}
              label="Estado"
              required={true}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Cód. IBGE"
              value={formData.codigo_municipio}
              onChange={(e) => handleInputChange('codigo_municipio', e.target.value)}
              required={true}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              label="Endereço"
              value={formData.endereco}
              onChange={(e) => handleInputChange('endereco', e.target.value)}
              disabled={loading}
              required={true}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Bairro"
              value={formData.bairro}
              onChange={(e) => handleInputChange('bairro', e.target.value)}
              disabled={loading}
              required={true}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Número"
              value={formData.numero}
              onChange={(e) => handleInputChange('numero', e.target.value)}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              label="Complemento"
              value={formData.complemento}
              onChange={(e) => handleInputChange('complemento', e.target.value)}
              disabled={loading}
            />
          </Grid>

        {/* Configurações */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5, mt: 1 }}>
            Configurações
          </Typography>
          <Divider sx={{ mb: 1 }} />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_default}
                onChange={(e) => handleInputChange('is_default', e.target.checked)}
                disabled={loading}
              />
            }
            label="Definir como escritório padrão"
          />
        </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Grid container spacing={2}>
          {/* Dados Fiscais */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5 }}>
              Dados Fiscais
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="CNPJ"
              value={formData.cnpj}
              onChange={(e) => handleCnpjChange(e.target.value)}
              disabled={loading}
              placeholder="00.000.000/0000-00"
              required={true}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="FISTEL/ANATEL"
              value={formData.fistel_anatel}
              onChange={(e) => handleInputChange('fistel_anatel', e.target.value)}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={4.5}>
            <TextField
              fullWidth
              size="small"
              label="Inscrição Estadual"
              value={formData.inscricao_estadual}
              onChange={(e) => handleInputChange('inscricao_estadual', e.target.value)}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} md={4.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Regime Tributário</InputLabel>
              <Select
                value={formData.regime_tributario}
                label="Regime Tributário"
                onChange={(e) => handleRegimeTributarioChange(e.target.value as string)}
                disabled={loading}
                size="small"
              >
                {regimesTributarios.map((regime) => (
                  <MenuItem key={regime.value} value={regime.value}>
                    {regime.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>  
            <TextField
              type="number"
              fullWidth
              size="small"
              label="Num. Proxima Nota"
              value={formData.num_proxima_nota}
              onChange={(e) => handleInputChange('num_proxima_nota', parseInt(e.target.value) || 1)}
              disabled={loading}
            />
          </Grid>

          {/* Alíquotas */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5, mt: 1 }}>
              Alíquotas (<Typography variant="caption">Verifique com seu contador</Typography>)
            </Typography>
            
            <Divider sx={{ mb: 1 }} />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Alíquota ICMS (%)"
              value={(formData.aliquota_icms)}
              onChange={(e) => {
                const valorFormatado = formatarPorcentagemOnInput(e.target.value);
                handleInputChange('aliquota_icms', valorFormatado.replace(',', '.'));
              }}
              disabled={loading}
              placeholder="0,00"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Alíquota PIS (%)"
              value={(formData.aliquota_pis)}
              onChange={(e) => {
                const valorFormatado = formatarPorcentagemOnInput(e.target.value);
                handleInputChange('aliquota_pis', valorFormatado.replace(',', '.'));
              }}
              disabled={loading}
              placeholder="0,00"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Alíquota COFINS (%)"
              value={(formData.aliquota_cofins)}
              onChange={(e) => {
                const valorFormatado = formatarPorcentagemOnInput(e.target.value);
                handleInputChange('aliquota_cofins', valorFormatado.replace(',', '.'));
              }}
              disabled={loading}
              placeholder="0,00"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Alíquota FUST (%)"
              value={(formData.aliquota_fust)}
              onChange={(e) => {
                const valorFormatado = formatarPorcentagemOnInput(e.target.value);
                handleInputChange('aliquota_fust', valorFormatado.replace(',', '.'));
              }}
              disabled={loading}
              placeholder="0,00"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Alíquota FUNTTEL (%)"
              value={(formData.aliquota_funttel)}
              onChange={(e) => {
                const valorFormatado = formatarPorcentagemOnInput(e.target.value);
                handleInputChange('aliquota_funttel', valorFormatado.replace(',', '.'));
              }}
              disabled={loading}
              placeholder="0,00"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          </Grid>

          {/* CST */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5, mt: 1 }}>
              Códigos de Situação Tributária
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>{formData.regime_tributario === 'simples_nacional' ? 'CSOSN' : 'CST ICMS'}</InputLabel>
              <Select
                value={formData.cst_icms}
                label={formData.regime_tributario === 'simples_nacional' ? 'CSOSN' : 'CST ICMS'}
                onChange={(e) => handleInputChange('cst_icms', e.target.value)}
                disabled={loading}
                size="small"
              >
                {cstIcmsOptions.map((cst) => (
                  <MenuItem key={cst.value} value={cst.value}>
                    {cst.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>CST PIS/COFINS</InputLabel>
              <Select
                value={formData.cst_pis_cofins}
                label="CST COFINS"
                onChange={(e) => handleInputChange('cst_pis_cofins', e.target.value)}
                disabled={loading}
                size="small"
              >
                {escritorioService.getCstPisCofinsOptions().map((cst) => (
                  <MenuItem key={cst.value} value={cst.value}>
                    {cst.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* CBS IBS */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5, mt: 1 }}>
              CBS e IBS
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>CST CBS/IBS</InputLabel>
              <Select
                value={formData.cst_cbs_ibs || ''}
                label="CST CBS/IBS"
                onChange={(e) => {
                  const v = e.target.value as string;
                  const allowedClass = new Set(
                    escritorioService.getCClassTribCbsIbsOptions(v).map((o) => o.value)
                  );
                  setFormData((prev) => ({
                    ...prev,
                    cst_cbs_ibs: v,
                    c_class_trib_cbs_ibs:
                      prev.c_class_trib_cbs_ibs && allowedClass.has(prev.c_class_trib_cbs_ibs)
                        ? prev.c_class_trib_cbs_ibs
                        : '',
                  }));
                  setError(null);
                }}
                disabled={loading}
                size="small"
              >
                <MenuItem value="">
                  <em>Nenhum</em>
                </MenuItem>
                {escritorioService.getCstCbsIbsOptions().map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Classificação CBS/IBS (cClassTrib)</InputLabel>
              <Select
                value={formData.c_class_trib_cbs_ibs || ''}
                label="Classificação CBS/IBS (cClassTrib)"
                onChange={(e) => handleInputChange('c_class_trib_cbs_ibs', e.target.value)}
                disabled={loading || cClassTribCbsIbsOptions.length === 0}
                size="small"
              >
                <MenuItem value="">
                  <em>Selecione</em>
                </MenuItem>
                {cClassTribCbsIbsOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Alíquota CBS (%)"
              value={(formData.aliquota_cbs)}
              onChange={(e) => {
                const valorFormatado = formatarPorcentagemOnInput(e.target.value);
                handleInputChange('aliquota_cbs', valorFormatado.replace(',', '.'));
              }}
              disabled={loading}
              placeholder="0,00"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Alíquota IBSUF (%)"
              value={(formData.aliquota_ibsuf)}
              onChange={(e) => {
                const valorFormatado = formatarPorcentagemOnInput(e.target.value);
                handleInputChange('aliquota_ibsuf', valorFormatado.replace(',', '.'));
              }}
              disabled={loading}
              placeholder="0,00"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Alíquota IBSMUN (%)"
              value={(formData.aliquota_ibsmun)}
              onChange={(e) => {
                const valorFormatado = formatarPorcentagemOnInput(e.target.value);
                handleInputChange('aliquota_ibsmun', valorFormatado.replace(',', '.'));
              }}
              disabled={loading}
              placeholder="0,00"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
            />
          </Grid>
          <Grid item xs={12} mt={1}>
           <TextField
             fullWidth
             size="small"
             label="Mensagem do Rodapé"
             value={formData.msg_rodape}
             onChange={(e) => handleInputChange('msg_rodape', e.target.value)}
             disabled={loading}
             multiline
             rows={3}
             placeholder="Digite a mensagem que aparecerá no rodapé dos documentos..."
             helperText="Máximo 1000 caracteres"
             inputProps={{
               maxLength: 1000
             }}
           />
         </Grid>

         <Grid item xs={12} mt={1}>
           <FormControl fullWidth size="small">
             <InputLabel>Descrição Padrão</InputLabel>
             <Select
               value={formData.descricao_padrao}
               label="Descrição Padrão"
               onChange={(e) => handleInputChange('descricao_padrao', e.target.value)}
               disabled={loading}
               size="small"
             >
               {escritorioService.getDescricaoPadraoOptions().map((opcao) => (
                 <MenuItem key={opcao.value} value={opcao.value}>
                   {opcao.label}
                 </MenuItem>
               ))}
             </Select>
           </FormControl>
         </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5 }}>
              Certificado Digital A1
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Certificado digital para assinatura de documentos fiscais.
            </Typography>
            <Divider sx={{ mb: 1 }} />
          </Grid>

          {escritorio?.certificado_digital_path && !mostrarFormCertificado && (
            <Grid item xs={12}>
              <Alert
                severity="info"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<SwapHorizIcon />}
                    onClick={() => setMostrarFormCertificado(true)}
                  >
                    Substituir
                  </Button>
                }
              >
                Este escritório já possui certificado cadastrado.
                {escritorio.certificado_digital_validade_ate && (
                  <> Validade até: <strong>{new Date(escritorio.certificado_digital_validade_ate + 'T12:00:00').toLocaleDateString('pt-BR')}</strong>.</>
                )}
              </Alert>
            </Grid>
          )}

          {(!escritorio?.certificado_digital_path && !mostrarFormCertificado) && (
            <Grid item xs={12}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setMostrarFormCertificado(true)}
                disabled={loading}
              >
                Adicionar certificado digital
              </Button>
            </Grid>
          )}

          {mostrarFormCertificado && (
            <>
              <Grid item xs={12}>
                <Button
                  size="small"
                  onClick={() => {
                    setMostrarFormCertificado(false);
                    setCertificadoFile(null);
                    setCertificadoSenha('');
                    setCertificadoValidado(false);
                  }}
                >
                  {escritorio?.certificado_digital_path ? 'Cancelar substituição' : 'Cancelar'}
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                  size="small"
                  disabled={loading}
                  sx={{ py: 1, borderStyle: 'dashed' }}
                >
                  {certificadoFile ? certificadoFile.name : 'Selecionar certificado A1'}
                  <input
                    type="file"
                    hidden
                    accept=".pfx,.p12,application/x-pkcs12,application/pkcs12"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      setCertificadoFile(file || null);
                      setCertificadoValidado(false);
                    }}
                  />
                </Button>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Senha do certificado"
                  value={certificadoSenha}
                  onChange={(e) => {
                    setCertificadoSenha(e.target.value);
                    setCertificadoValidado(false);
                  }}
                  disabled={loading}
                  placeholder="Digite a senha do certificado"
                  helperText="Obrigatória para validar e salvar o certificado"
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={2} alignItems="center">
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    startIcon={certificadoValidado ? <CheckCircleIcon /> : <SecurityIcon />}
                    onClick={handleValidarCertificado}
                    disabled={!certificadoFile || !certificadoSenha.trim() || loading || validandoCertificado}
                  >
                    {validandoCertificado ? 'Validando...' : certificadoValidado ? 'Certificado válido' : 'Validar certificado'}
                  </Button>
                  {certificadoValidado && (
                    <Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CheckCircleIcon fontSize="small" />
                      Senha e validade verificados. Pode salvar o escritório.
                    </Typography>
                  )}
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </TabPanel>

      {/* Botões de Ação - Fora das Abas */}
      <Box 
        display="flex" 
        gap={2} 
        justifyContent="flex-end" 
        mt={3} 
        mb={2}
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          pt: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Button
          variant="outlined"
          size="medium"
          startIcon={<CancelIcon />}
          onClick={onCancel}
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="medium"
          startIcon={<SaveIcon />}
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? 'Salvando...' : escritorio ? 'Atualizar' : 'Salvar'}
        </Button>
      </Box>

      {/* Modal para Herdar Dados */}
      <Dialog 
        open={modalHerdarDados} 
        onClose={() => setModalHerdarDados(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <CopyAllIcon />
            Herdar Dados de Outro Escritório
          </Box>
        </DialogTitle>
        
        <DialogContent>
         
                      <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Dados que serão copiados:
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              <li>Nome Fantasia</li>
              <li>Razão Social</li>
              <li>CNPJ</li>
              <li>FISTEL/ANATEL</li>
              <li>Inscrição Estadual</li>
              <li>Regime Tributário</li>
              <li>Responsável</li>
              <li>Configurações Fiscais</li>
            </Box>
          </Box>
          
          <EscritorioSelect
            value={escritorioSelecionado}
            onChange={(value) => setEscritorioSelecionado(value as number)}
            label="Escritório de origem"
            placeholder="Selecione o escritório para copiar dados"
            size="small"
            required
            onlyActive={true}
            allowClear={true}
          />
        </DialogContent>
        
        <DialogActions>
          <Button
            onClick={() => {
              setModalHerdarDados(false);
              setEscritorioSelecionado(null);
            }}
            size="small"
            disabled={carregandoHeranca}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleHerdarDados}
            variant="contained"
            size="small"
            startIcon={<CopyAllIcon />}
            disabled={!escritorioSelecionado || carregandoHeranca}
          >
            {carregandoHeranca ? 'Copiando...' : 'Copiar Dados'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EscritorioForm;
